/**
 * Bypass Authentication Utilities
 * 
 * Development-only feature to test the app UI without authentication.
 * This allows viewing the main app without login, but still requires
 * authentication for creating/entering workspaces.
 * 
 * To enable: Set REACT_APP_BYPASS_AUTH=true in .env.local
 * 
 * When enabled:
 * - App UI can be viewed without authentication
 * - Can manually enter workspace ID to test workspace views
 * - Workspace creation still requires authentication
 * - Workspace entry still requires authentication
 */

import { shouldUseBypassAuth as checkShouldUseBypassAuth } from "./devModeType";

/**
 * Check if bypass auth is enabled
 * Only works in development mode AND when dev mode type is "web-mobile"
 */
export const isBypassAuthEnabled = (): boolean => {
  return checkShouldUseBypassAuth();
};

/**
 * Default development workspace name to search for
 * When bypass auth is enabled, the app will automatically search for and load this workspace
 */
const DEFAULT_DEV_WORKSPACE_NAME = "Dev_Workspace";

/**
 * Fallback development workspace ID (if workspace name search fails)
 * This workspace will be used if we can't find a workspace by name
 */
const FALLBACK_DEV_WORKSPACE_ID = "90Yq3rWMUo50j8kXOzr7";

/**
 * Get bypass workspace ID from URL parameter, localStorage, or default
 */
export const getBypassWorkspaceId = (): string | null => {
  if (!isBypassAuthEnabled()) {
    return null;
  }

  // Check URL parameter first
  const urlParams = new URLSearchParams(window.location.search);
  const workspaceId = urlParams.get("workspaceId");
  if (workspaceId) {
    localStorage.setItem("bypassWorkspaceId", workspaceId);
    return workspaceId;
  }

  // Check localStorage for stored workspace ID
  const storedId = localStorage.getItem("bypassWorkspaceId");
  if (storedId) {
    return storedId;
  }

  // Use fallback development workspace ID
  // The App.tsx will try to find "Dev_Workspace" by name first
  // Save it to localStorage so it persists
  localStorage.setItem("bypassWorkspaceId", FALLBACK_DEV_WORKSPACE_ID);
  return FALLBACK_DEV_WORKSPACE_ID;
};

/**
 * Get the default dev workspace name to search for
 */
export const getDefaultDevWorkspaceName = (): string => {
  return DEFAULT_DEV_WORKSPACE_NAME;
};

/**
 * Set bypass workspace ID
 */
export const setBypassWorkspaceId = (workspaceId: string | null): void => {
  if (!isBypassAuthEnabled()) {
    return;
  }

  if (workspaceId) {
    localStorage.setItem("bypassWorkspaceId", workspaceId);
  } else {
    localStorage.removeItem("bypassWorkspaceId");
  }
};
